import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BinderBuildersDisclaimer from "../components/BinderBuildersDisclaimer";
import { getProductBySlug } from "../data/binderBuildersProducts";
import { fetchBinderBuildersInventory } from "../lib/binderBuildersSheetInventory";

export default function BinderBuildersProduct() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const fallbackInventory = product?.inventory || [];
  const [inventory, setInventory] = useState(fallbackInventory);
  const [isLoadingInventory, setIsLoadingInventory] = useState(Boolean(product));

  useEffect(() => {
    if (!product) {
      return undefined;
    }

    let isCurrentProduct = true;
    setInventory(product.inventory || []);
    setIsLoadingInventory(true);

    fetchBinderBuildersInventory()
      .then((inventoryByProduct) => {
        const liveInventory = inventoryByProduct[slug];
        if (isCurrentProduct && liveInventory?.length) {
          setInventory(liveInventory);
        }
      })
      .catch(() => {
        // Keep the local inventory visible when the published sheet is unavailable or invalid.
      })
      .finally(() => {
        if (isCurrentProduct) {
          setIsLoadingInventory(false);
        }
      });

    return () => {
      isCurrentProduct = false;
    };
  }, [product, slug]);

  if (!product) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.panelNotFound}>
            <h1 style={styles.notFoundTitle}>Pack not found</h1>
            <p style={styles.notFoundCopy}>That pack doesn't exist. Head back to inventory.</p>
            <Link to="/binder-builders" style={styles.linkBack}>
              Back to inventory
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.brandBar}>
          <Link to="/binder-builders" style={styles.brandLink}>
            <img
              src="/images/new-binder-builders-logo.png"
              alt="B's Binder Builders"
              style={styles.brandLogo}
            />
            <span>Back to the Binder Builders lineup</span>
          </Link>
        </div>
        <header style={styles.header}>
          <div style={styles.eyebrow}>Collector lineup</div>
          <div style={styles.titleRow}>
            <div>
              <h1 style={styles.h1}>{product.name}</h1>
              {product.subHeading && (
                <p style={styles.subHeading}>{product.subHeading}</p>
              )}
            </div>
            {product.priceLabel && (
              <div style={styles.priceLabel}>{product.priceLabel}</div>
            )}
          </div>
          {product.heroLine && (
            <p style={styles.heroLine}>{product.heroLine}</p>
          )}
          <p style={styles.description}>{product.description}</p>
        </header>

        <div style={styles.divider} aria-hidden="true" />

        <section style={styles.panel}>
          <h2 style={styles.sectionTitle}>What's included</h2>
          <ul style={styles.bulletList}>
            {product.bullets.map((b, i) => (
              <li key={i} style={styles.bulletItem}>{b}</li>
            ))}
          </ul>
        </section>

        {product.options && product.options.length > 0 && (
          <section style={styles.panel}>
            <h2 style={styles.sectionTitle}>More ways to order</h2>
            <div style={styles.optionsGrid}>
              {product.options.map((option) => (
                <article key={option.name} style={styles.optionCard}>
                  <div style={styles.optionTitleRow}>
                    <h3 style={styles.optionTitle}>{option.name}</h3>
                    {option.priceLabel && (
                      <div style={styles.optionPrice}>{option.priceLabel}</div>
                    )}
                  </div>
                  <p style={styles.optionDescription}>{option.description}</p>
                  <ul style={styles.optionBullets}>
                    {option.bullets.map((bullet) => (
                      <li key={bullet} style={styles.optionBulletItem}>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <Link to="/binder-builders#order-inquiry" style={styles.optionCta}>
                    {option.ctaLabel}
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        <section style={styles.panel}>
          <h2 style={styles.sectionTitle}>Available inventory</h2>
          {isLoadingInventory && <p style={styles.inventoryStatus}>Checking live inventory...</p>}
          <div style={styles.inventoryScroll}>
            <table style={styles.inventoryTable}>
              <thead>
                <tr>
                  <th style={styles.inventoryHeader}>Name</th>
                  <th style={styles.inventoryHeader}>Description</th>
                  <th style={styles.inventoryHeaderQuantity}>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item, index) => (
                  <tr key={item.name} style={index % 2 === 0 ? styles.inventoryRow : styles.inventoryRowAlt}>
                    <td style={styles.inventoryCellName}>{item.name}</td>
                    <td style={styles.inventoryCell}>{item.description}</td>
                    <td style={styles.inventoryCellQuantity}>{item.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section style={styles.panel}>
          <h2 style={styles.sectionTitle}>Photos</h2>
          {product.photos && product.photos.length > 0 ? (
            <div style={styles.photosGrid}>
              {product.photos.map((src, i) => (
                <img key={src} src={src} alt={`${product.name} ${i + 1}`} style={styles.photo} />
              ))}
            </div>
          ) : (
            <div style={styles.photoPlaceholder}>Photos coming soon</div>
          )}
        </section>

        <div style={styles.ctaBlock}>
          <Link to="/binder-builders#order-inquiry" style={styles.ctaCallout}>
            {product.ctaLabel || "Reach out to confirm availability"}
          </Link>
        </div>

        <BinderBuildersDisclaimer style={styles.disclaimer} />

        <div style={styles.backLink}>
          <Link to="/binder-builders" style={styles.linkBack}>Back to inventory</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    overflowX: "hidden",
    background: "#1F2528",
    color: "#F7F3E8",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    padding: "clamp(16px, 4vw, 40px) 16px",
  },

  shell: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 900,
    margin: "0 auto",
    padding: "clamp(8px, 2vw, 20px)",
  },

  brandBar: {
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
    padding: "10px 14px",
    border: "1px solid #6897A9",
    borderRadius: 14,
    background: "#283034",
  },

  brandLink: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    color: "#F7F3E8",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
  },

  brandLogo: {
    width: 116,
    height: 48,
    objectFit: "cover",
    objectPosition: "center",
    border: "1px solid #F0DBA5",
    borderRadius: 9,
    background: "#F7F3E8",
  },

  header: {
    boxSizing: "border-box",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    marginBottom: 22,
    padding: "clamp(22px, 4vw, 34px)",
    border: "2px solid #6897A9",
    borderRadius: 18,
    background: "#F7F3E8",
    boxShadow: "0 10px 26px rgba(0, 0, 0, 0.2)",
  },

  eyebrow: {
    display: "inline-block",
    marginBottom: 12,
    padding: "5px 10px",
    border: "1px solid #6897A9",
    borderRadius: 999,
    color: "#385D6B",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },

  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 10,
  },

  h1: {
    margin: 0,
    fontSize: "clamp(32px, 6vw, 52px)",
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: "-0.035em",
    color: "#1F2528",
  },

  subHeading: {
    margin: "5px 0 0 0",
    color: "#6897A9",
    fontSize: 16,
    fontWeight: 800,
  },

  priceLabel: {
    background: "#F0DBA5",
    border: "1px solid #6897A9",
    color: "#1F2528",
    fontWeight: 900,
    fontSize: 15,
    padding: "4px 12px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },

  heroLine: {
    margin: "0 0 10px 0",
    fontSize: 17,
    fontWeight: 600,
    color: "#1F2528",
    lineHeight: 1.5,
  },

  description: {
    margin: 0,
    fontSize: 15,
    lineHeight: 1.65,
    color: "#636466",
  },

  divider: {
    height: 2,
    margin: "24px 0",
    borderRadius: 2,
    background: "#6897A9",
    opacity: 0.7,
  },

  panel: {
    boxSizing: "border-box",
    width: "100%",
    background: "#F7F3E8",
    border: "1px solid #6897A9",
    borderRadius: 14,
    padding: "clamp(18px, 3vw, 26px)",
    marginBottom: 18,
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.16)",
  },

  panelNotFound: {
    boxSizing: "border-box",
    width: "100%",
    background: "#F7F3E8",
    border: "2px solid #6897A9",
    borderRadius: 16,
    padding: "clamp(28px, 7vw, 64px)",
    textAlign: "center",
    boxShadow: "0 10px 26px rgba(0, 0, 0, 0.2)",
  },

  notFoundTitle: {
    margin: "0 0 8px 0",
    fontSize: 24,
    fontWeight: 900,
    color: "#1F2528",
  },

  notFoundCopy: {
    margin: "0 0 16px 0",
    color: "#636466",
  },

  sectionTitle: {
    margin: "0 0 12px 0",
    fontSize: 15,
    fontWeight: 800,
    color: "#385D6B",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  bulletList: {
    margin: 0,
    paddingLeft: 20,
  },

  bulletItem: {
    marginBottom: 8,
    fontSize: 15,
    color: "#1F2528",
  },

  inventoryScroll: {
    overflowX: "auto",
    border: "1px solid #6897A9",
    borderRadius: 10,
  },

  inventoryStatus: {
    margin: "0 0 12px 0",
    color: "#636466",
    fontSize: 14,
  },

  inventoryTable: {
    width: "100%",
    minWidth: 560,
    borderCollapse: "collapse",
    color: "#1F2528",
    fontSize: 14,
  },

  inventoryHeader: {
    background: "#6897A9",
    color: "#F7F3E8",
    padding: "10px 12px",
    textAlign: "left",
    borderBottom: "2px solid #1F2528",
  },

  inventoryHeaderQuantity: {
    background: "#6897A9",
    color: "#F7F3E8",
    padding: "10px 12px",
    textAlign: "right",
    borderBottom: "2px solid #1F2528",
  },

  inventoryRow: {
    background: "#F7F3E8",
  },

  inventoryRowAlt: {
    background: "#E7EEF0",
  },

  inventoryCell: {
    padding: "11px 12px",
    borderBottom: "1px solid rgba(99, 100, 102, 0.3)",
    verticalAlign: "top",
    lineHeight: 1.45,
  },

  inventoryCellName: {
    padding: "11px 12px",
    borderBottom: "1px solid rgba(99, 100, 102, 0.3)",
    verticalAlign: "top",
    fontWeight: 800,
    lineHeight: 1.45,
  },

  inventoryCellQuantity: {
    padding: "11px 12px",
    borderBottom: "1px solid rgba(99, 100, 102, 0.3)",
    verticalAlign: "top",
    textAlign: "right",
    fontWeight: 900,
    lineHeight: 1.45,
    color: "#1F2528",
  },

  photosGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 12,
  },

  photo: {
    width: "100%",
    height: "auto",
    borderRadius: 14,
    border: "1px solid #6897A9",
  },

  photoPlaceholder: {
    border: "2px dashed #6897A9",
    borderRadius: 10,
    padding: 28,
    textAlign: "center",
    color: "#636466",
  },

  ctaBlock: {
    marginBottom: 20,
  },

  ctaCallout: {
    display: "inline-block",
    background: "#F0DBA5",
    border: "1px solid #6897A9",
    color: "#1F2528",
    fontWeight: 900,
    fontSize: 15,
    padding: "12px 20px",
    borderRadius: 10,
    textDecoration: "none",
  },

  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },

  optionCard: {
    border: "1px solid rgba(104, 151, 169, 0.7)",
    borderRadius: 10,
    padding: 16,
    background: "#FFFFFF",
  },

  optionTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8,
  },

  optionTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 900,
    color: "#1F2528",
  },

  optionPrice: {
    background: "#F0DBA5",
    color: "#1F2528",
    fontWeight: 900,
    fontSize: 13,
    padding: "3px 9px",
    borderRadius: 16,
    whiteSpace: "nowrap",
  },

  optionDescription: {
    margin: "0 0 10px 0",
    color: "#636466",
    fontSize: 14,
    lineHeight: 1.5,
  },

  optionBullets: {
    margin: "0 0 12px 0",
    paddingLeft: 18,
  },

  optionBulletItem: {
    marginBottom: 6,
    color: "#1F2528",
    fontSize: 14,
    lineHeight: 1.45,
  },

  optionCta: {
    display: "inline-block",
    color: "#1F2528",
    background: "#F0DBA5",
    border: "1px solid #6897A9",
    padding: "8px 12px",
    borderRadius: 11,
    textDecoration: "none",
    fontWeight: 800,
    fontSize: 14,
  },

  backLink: {
    marginTop: 14,
  },

  linkBack: {
    display: "inline-block",
    color: "#F0DBA5",
    textDecoration: "none",
    fontWeight: 800,
    fontSize: 15,
  },

  disclaimer: {
    marginBottom: 16,
  },
};
